import {
  Button,
  Card,
  Flex,
  Heading,
  IconButton,
  Select,
  Table,
  Text,
  Tooltip,
} from '@radix-ui/themes'
import { PluginDefinitionResponse, PluginMappingResponse } from '@Generated/back-office-client/src'
import { PluginJsonSchema } from './plugin-schema-types'
import { EyeOpenIcon, EyeClosedIcon } from '@radix-ui/react-icons'
import { PluginSlot } from './types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PluginServiceAPI } from '@API/data-services/plugin-service'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config/types'
import { useState } from 'react'
import { WithPermissions } from '@Components/common/with-permissions'
import { AuthScopes } from '@Generated/back-office-client/src'
import { PluginConfigEditor } from './plugin-config-editor'
import { coerceConfigValues } from './plugin-config-coercion'

type PluginSectionProps = {
  slot: PluginSlot
  mapping?: PluginMappingResponse
  definitions: PluginDefinitionResponse[]
  onSaved: () => void
}

export const PluginSection = ({ slot, mapping, definitions, onSaved }: PluginSectionProps) => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [selectedPluginId, setSelectedPluginId] = useState<string | undefined>(mapping?.pluginId)
  const [configValues, setConfigValues] = useState<Record<string, unknown>>({})
  const [configErrors, setConfigErrors] = useState<Record<string, string> | undefined>()

  const { data: mappingConfig } = useQuery({
    queryKey: [`${activeCustomerId}-plugin-mapping-config-${slot.type}-${slot.refId || 'default'}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return PluginServiceAPI.getMappingConfig(slot.type, slot.refId)
    },
    enabled: !!mapping && mapping.hasConfig && !editing,
  })

  const { data: schema } = useQuery({
    queryKey: [
      `${activeCustomerId}-plugin-schema-${selectedPluginId || mapping?.pluginId || 'none'}`,
    ],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      const pluginId = editing ? selectedPluginId : mapping?.pluginId
      if (!pluginId) {
        return null
      }
      return PluginServiceAPI.getSchema(pluginId)
    },
    enabled: !!(editing ? selectedPluginId : mapping?.pluginId),
  })

  const { mutate: upsertMapping, isPending } = useMutation({
    mutationFn: async (coercedValues: Record<string, unknown>) => {
      if (!selectedPluginId) {
        throw new Error('No plugin selected')
      }
      return PluginServiceAPI.upsertMapping(slot.type, selectedPluginId, slot.refId, coercedValues)
    },
    onSuccess: () => {
      setEditing(false)
      queryClient.invalidateQueries({
        queryKey: [
          `${activeCustomerId}-plugin-mapping-config-${slot.type}-${slot.refId || 'default'}`,
        ],
      })
      onSaved()
    },
  })

  const handleEdit = () => {
    setSelectedPluginId(mapping?.pluginId)
    if (mappingConfig?.config) {
      setConfigValues(mappingConfig.config)
    } else {
      setConfigValues({})
    }
    setConfigErrors(undefined)
    setRevealed(false)
    setEditing(true)
  }

  const handleCancel = () => {
    setEditing(false)
    setRevealed(false)
    setSelectedPluginId(mapping?.pluginId)
    setConfigValues({})
    setConfigErrors(undefined)
  }

  const handlePluginChange = (newPluginId: string) => {
    setSelectedPluginId(newPluginId)
    if (newPluginId !== mapping?.pluginId) {
      setConfigValues({})
    }
  }

  const handleSave = () => {
    if (!schema) {
      upsertMapping(configValues)
      return
    }
    const stringValues: Record<string, string> = {}
    for (const [key, value] of Object.entries(configValues)) {
      stringValues[key] = String(value ?? '')
    }
    const result = coerceConfigValues(stringValues, schema)
    if (!result.success) {
      setConfigErrors(result.errors)
      return
    }
    setConfigErrors(undefined)
    setConfigValues(result.data)
    upsertMapping(result.data)
  }

  const isValid = () => {
    if (!selectedPluginId) {
      return false
    }
    if (schema?.required) {
      return schema.required.every((key) => configValues[key])
    }
    return true
  }

  const findDefinitionName = (pluginId: string): string => {
    const definition = definitions.find((def) => def.id === pluginId)
    return definition?.name || pluginId
  }

  const renderConfig = (config: Record<string, unknown>, configSchema: PluginJsonSchema) => {
    if (!configSchema.properties) {
      return null
    }

    return (
      <Table.Root style={{ tableLayout: 'fixed' }}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell style={{ width: '30%' }}>Key</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Object.entries(configSchema.properties).map(([key, propertyDef]) => {
            const propertySchema = typeof propertyDef === 'boolean' ? undefined : propertyDef
            const label = propertySchema?.description || key
            const value = config[key]

            return (
              <Table.Row key={key}>
                <Table.Cell>{label}</Table.Cell>
                <Table.Cell style={{ wordBreak: 'break-all' }}>
                  {revealed ? String(value ?? '') : '••••••••'}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    )
  }

  const renderPluginName = () => {
    if (editing) {
      return (
        <Select.Root value={selectedPluginId} onValueChange={handlePluginChange}>
          <Select.Trigger />
          <Select.Content>
            {definitions.map((definition) => (
              <Select.Item key={definition.id} value={definition.id}>
                {definition.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      )
    }
    if (!mapping) {
      return <Text color="gray">Not Configured</Text>
    }
    return <Text weight="medium">{findDefinitionName(mapping.pluginId)}</Text>
  }

  const renderConfigSection = () => {
    if (editing && schema && schema.properties) {
      return (
        <PluginConfigEditor
          schema={schema}
          values={configValues}
          onChange={setConfigValues}
          errors={configErrors}
        />
      )
    }
    if (!editing && mappingConfig?.config && schema) {
      return renderConfig(mappingConfig.config, schema)
    }
    return null
  }

  const renderActions = () => {
    if (editing) {
      return (
        <Flex gap="2">
          <Button onClick={handleSave} disabled={isPending || !isValid()}>
            Save
          </Button>
          <Button variant="soft" onClick={handleCancel}>
            Cancel
          </Button>
        </Flex>
      )
    }
    return (
      <Flex gap="2">
        {mappingConfig?.config && (
          <Tooltip content={revealed ? 'Hide values' : 'Reveal values'}>
            <IconButton variant="soft" onClick={() => setRevealed(!revealed)}>
              {revealed ? <EyeClosedIcon /> : <EyeOpenIcon />}
            </IconButton>
          </Tooltip>
        )}
        <WithPermissions requiredPermissions={AuthScopes.writeplugins}>
          <Button onClick={handleEdit}>Edit</Button>
        </WithPermissions>
      </Flex>
    )
  }

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Heading size="4">{slot.label}</Heading>
        {renderPluginName()}
        {renderConfigSection()}
        {renderActions()}
      </Flex>
    </Card>
  )
}
